import { User } from '../../../types/User';
import {
    Checkbox,
    CheckboxProps,
    DefaultFooterComponent,
    PopupFooterProps,
    PopupStateProps,
    showAlert
} from '@silverfort/sf-components';
import { FC, useCallback } from 'react';
import { usePopup } from '../../../context/PopupProvider';
import { useUnmatchUsers } from '../../../api';

interface Params {
    onUnmatchedUsers: () => void;
}

interface UnmatchUsersPopupState {
    selectedUsers: User['guid'][];
}

type ContentProps = PopupStateProps<UnmatchUsersPopupState> & {
    users?: User[];
};

const Content: FC<ContentProps> = ({ users, popupState, setPopupState }) => {
    const { selectedUsers = [] } = popupState ?? {};

    const handleAllOptionChange: CheckboxProps['onChange'] = (event) => {
        setPopupState(
            event.target.checked
                ? { selectedUsers: users ? users.map((user) => user.guid) : [] }
                : { selectedUsers: [] }
        );
    };

    const handleChangeUserSelection = (userGuid: User['guid'], checked: boolean) => {
        setPopupState(({ selectedUsers: prevSelectedUsers }) => {
            if (checked) {
                return {
                    selectedUsers: [...prevSelectedUsers, userGuid]
                };
            }

            return {
                selectedUsers: prevSelectedUsers.filter((user) => user !== userGuid)
            };
        });
    };

    return (
        <div className="w-full flex flex-col gap-2">
            <span className="font-sf text-13 leading-[19px] tracking-[0.203px]">
                Select the user you want to unmatch
            </span>
            <div className="flex flex-col rounded-[8px] border border-sf-gray-100">
                <div className="ps-2 text-sf h-12 border-b border-sf-gray-100 flex items-center font-medium">
                    <Checkbox
                        label="All"
                        checked={selectedUsers && users && selectedUsers.length === users.length}
                        onChange={handleAllOptionChange}
                    />
                </div>
                {users?.map((user) => (
                    <div
                        key={user.guid}
                        className="ps-2 text-sf h-12 border-b last:border-b-0 border-sf-gray-100 flex items-center">
                        <Checkbox
                            label={user.userName}
                            checked={selectedUsers.includes(user.guid)}
                            onChange={(event) => handleChangeUserSelection(user.guid, event.target.checked)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

type FooterProps = PopupFooterProps<UnmatchUsersPopupState>;

const Footer: FC<FooterProps> = ({ popupState, ...footerProps }) => {
    const { selectedUsers = [] } = popupState ?? {};

    return (
        <DefaultFooterComponent
            primaryButtonProps={{
                text: 'Unmatch',
                disabled: !selectedUsers.length
            }}
            {...footerProps}
        />
    );
};

export const useUnmatchUsersPopup = ({ onUnmatchedUsers }: Params) => {
    const { openPopup, closePopup } = usePopup();
    const { unmatchUsers } = useUnmatchUsers({
        onSuccess: useCallback(() => {
            showAlert('Users are now unmatched. Token sharing for these users has been disabled.', 'success', {
                title: 'Users unmatched '
            });

            onUnmatchedUsers();
        }, [onUnmatchedUsers]),
        onError: useCallback(() => {
            showAlert('An error occurred while unmatching users.', 'error', { title: 'Failed to unmatch users' });
        }, [])
    });

    const openUnmatchUsersPopup = useCallback(
        (secondaryUsers: User[]) => {
            openPopup({
                popupClassName: 'w-[360px]',
                title: 'Unmatch account',
                initialState: { selectedUsers: [] },
                ContentComponent: Content,
                ContentComponentProps: { users: secondaryUsers },
                FooterComponent: Footer,
                onApprove: (state) => {
                    const { selectedUsers = [] } = state ?? {};
                    unmatchUsers(selectedUsers);
                }
            });
        },
        [openPopup, closePopup, unmatchUsers]
    );

    return {
        openUnmatchUsersPopup
    };
};
